import React, { useState } from 'react'
import { MessageSquare, RotateCcw, PanelLeft, Moon, Sun } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { GoToHubSpotButton } from '@/components/ui/GoToHubSpotButton'

interface ChatTopBarProps {
  title: string
  onClear: () => void
  onOpenConversations?: () => void
  notificationBell?: React.ReactNode
  /** When set, shows an "Open in HubSpot" button — for record-seeded conversations. */
  hubspotUrl?: string | null
}

export const ChatTopBar: React.FC<ChatTopBarProps> = ({ title, onClear, onOpenConversations, notificationBell, hubspotUrl }) => {
  const { currentTheme, isDarkMode, setIsDarkMode } = useTheme()
  const [confirmClear, setConfirmClear] = useState(false)

  return (
    <div
      className="flex items-center justify-between h-14 px-4 md:px-6 border-b shrink-0"
      style={{
        backgroundColor: currentTheme.secondary,
        borderColor: currentTheme.border,
      }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <MessageSquare className="w-4 h-4 shrink-0" style={{ color: currentTheme.accent }} />
        <h2
          className="text-sm font-medium truncate"
          style={{ color: currentTheme.text }}
        >
          {title}
        </h2>
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <button
          onClick={onOpenConversations}
          className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-150"
          style={{ color: currentTheme.mutedText }}
          aria-label="Open conversations"
        >
          <PanelLeft className="w-4 h-4" />
        </button>

        {hubspotUrl && <GoToHubSpotButton href={hubspotUrl} />}

        {notificationBell}

        <button
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-150"
          style={{ color: currentTheme.mutedText }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = currentTheme.hoverBackground
            e.currentTarget.style.color = currentTheme.text
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.color = currentTheme.mutedText
          }}
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label="Toggle theme"
        >
          {isDarkMode ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
        </button>

        <button
          onClick={() => setConfirmClear(true)}
          className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-150"
          style={{ color: currentTheme.mutedText }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = currentTheme.hoverBackground
            e.currentTarget.style.color = currentTheme.text
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.color = currentTheme.mutedText
          }}
          title="Clear conversation"
          aria-label="Clear conversation"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      </div>

      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent
          className="py-6 px-8 rounded-2xl shadow-xl max-w-sm w-full border-none"
          style={{ background: currentTheme.secondary, color: currentTheme.text }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-center" style={{ color: currentTheme.text }}>
              Clear this conversation?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-center mt-1" style={{ color: currentTheme.mutedText }}>
            All messages in "{title}" will be permanently deleted. This cannot be undone.
          </p>
          <DialogFooter className="flex justify-center gap-3 mt-6 flex-row sm:justify-center">
            <Button
              variant="secondary"
              onClick={() => setConfirmClear(false)}
              className="px-5 py-2 rounded-lg font-medium h-auto text-sm"
              style={{ background: currentTheme.hoverBackground, color: currentTheme.text, border: `1px solid ${currentTheme.border}` }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => { onClear(); setConfirmClear(false) }}
              className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium h-auto text-sm"
            >
              Clear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
